import { IShippingAddress } from '../types/index'
import { CreatePickUpsResponse } from '../types/Courier/UPS/UPSCreatePickUpsResponse'
import { UPSPlannedPickupDateAndTime } from '../types/Courier/UPS/UPSPlannedPickupDateAndTime'
import { mapState } from '../utils/helpers' 
import { raygun } from '@ynap/clientside-monitoring'

type PackageObject = {
    ServiceCode: string,
    Quantity: string,
}

const mapServiceCode = (productCode: string) => {
    // NOTE: There is no clear mapping of what this should be. Tried to use the 
    // UPS TimeInTransit documentation but suggested mapping didn't work for
    // prod API request from India. Used this instead: https://www.npmjs.com/package/ups-service-codes
    if(productCode == "UPS Ground") return '003'; 
    if(productCode == "UPS Standard") return '011';
    if(productCode == "UPS Express Saver") return '065';
    if(productCode == "UPS Worldwide Express Saver") return '007';
    else return "";
}

const reportToRaygun = (message, { requestBody, error, trackingUrl, returnTrackingUrl }) =>
    raygun.helpers.agent('send', {
        error: new Error(message),
        tags: ['UPS', 'book-a-return', 'create-ups-pickups-error', requestBody.PickupCreationRequest.PickupAddress.CountryCode],
        customData: {
            requestBody,
            errorString: error.toString(),
            trackingUrl, returnTrackingUrl
        },
    })

const createPickUps = async(
    config: any,
    shippingAddress: IShippingAddress,
    plannedPickupDateAndTime: string | UPSPlannedPickupDateAndTime | undefined,
    numberOfBoxes: number,
    specialInstructions: string,
    productCode: string | undefined,
    trackingUrl: string, 
    returnTrackingUrl: string,
    externalOrderId: string
): Promise<CreatePickUpsResponse> => {
    const clientId = config.global?.services.commerce.clientId
    const { collectionService } = config.head
    const { baseUrl } = collectionService.couriers.UPS
    const { country, city, zipCode, addressLine, email1, phone1, firstName, lastName, state } = shippingAddress
    const stateProvince = country === "CA" ? mapState(state) : state;
    const serviceCode = productCode ? mapServiceCode(productCode) : ""

    const packageObject:PackageObject = {
        ServiceCode: serviceCode,
        Quantity: numberOfBoxes.toString(),
    }

    const body = {
        PickupCreationRequest: {
            PickupDateInfo: plannedPickupDateAndTime,
            PickupAddress: {
                ContactName: `${firstName} ${lastName}`.substring(0, 22), // UPS only accepts 22 characters
                AddressLine: addressLine[0],
                City: city,
                StateProvince: stateProvince?.length >= 2 ? stateProvince : '', // UPS only allow state code (e.g. NJ) or province (for Ireland)
                PostalCode: zipCode,
                CountryCode: country,
                Phone: {
                  Number: phone1,
                }
            },
            PickupPiece: [
                packageObject
            ],
            SpecialInstruction: specialInstructions,
            Notification: {
              ConfirmationEmailAddress: email1
            },
            OrderId: externalOrderId,
        }
    }
   
    const createPickups =
        fetch(`${baseUrl}/pickup`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'content-type': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'x-ibm-client-id': clientId,
                },
        }).then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                reportToRaygun(`Unsucessful response when hitting ${baseUrl}/pickup`, { requestBody: body, error, trackingUrl, returnTrackingUrl })
                throw new Error(error)
            }
            return res.json()
        }).catch(e => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/pickup`, { requestBody: body, error: e, trackingUrl, returnTrackingUrl })
            throw new Error(e)
        })

    return createPickups
}

export default createPickUps
